import axios from 'axios';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import io from 'socket.io-client';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const sessionToken = localStorage.getItem('sessionToken');
    const userData = localStorage.getItem('userData');
    
    if (sessionToken && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (userData, sessionToken) => {
    localStorage.setItem('sessionToken', sessionToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userData');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Dark mode context
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const handleAuthRedirect = () => {
  // const previewUrl = window.location.origin;
  // const authUrl = `https://auth.dubcora.com/?redirect=${encodeURIComponent(previewUrl + '/profile')}`;
  // window.location.href = authUrl;
};

const Navigation = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
      <nav className={`fixed w-full z-50 transition-all duration-300 ${darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} backdrop-blur-md border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">BV</span>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                BookMyVakeel
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="hover:text-blue-600 transition-colors">Features</a>
              <a href="#about" className="hover:text-blue-600 transition-colors">About</a>
              <a href="#contact" className="hover:text-blue-600 transition-colors">Contact</a>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={handleAuthRedirect}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105"
              >
                Login / Register
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={handleAuthRedirect}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
}

const Hero = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
      <section className={`py-20 pt-24 pb-20 px-4 ${darkMode ? 'bg-gradient-to-br from-black-90 via-black to-black-50' : 'bg-gradient-to-br from-blue-50 via-white to-blue-50'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Your Legal Expert,
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent block">
                  Just a Click Away
                </span>
              </h1>
              <p className={`text-xl leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Connect with verified advocates instantly. Get expert legal consultation through secure <strong>real-time chat</strong> or calls. Your justice, our priority.
              </p>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 bg-green-50 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-green-700 font-medium">24/7 Available</span>
              </div>
              <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-blue-700 font-medium">Verified Advocates</span>
              </div>
              <div className="flex items-center space-x-2 bg-purple-50 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span className="text-purple-700 font-medium">Secure & Confidential</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">50+</div>
                <div className="text-gray-600">Expert Advocates</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">50+</div>
                <div className="text-gray-600">Cases Resolved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">4.8/5</div>
                <div className="text-gray-600">Client Rating</div>
              </div>
            </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleAuthRedirect}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Consultation
                </button>
                <button className={`px-8 py-4 rounded-lg text-lg font-semibold border-2 transition-all ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  Learn More
                </button>
              </div>
            </div>
            <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-3xl transform rotate-3"></div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1662104935883-e9dd0619eaba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxsYXd5ZXJ8ZW58MHx8fGJsdWV8MTc1NDE5ODg3MHww&ixlib=rb-4.1.0&q=85" 
                  alt="Professional Legal Consultation" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-purple-600/20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
}

const Features = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
      <section id="features" className={`py-20 ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">BookMyVakeel</span>?
            </h2>
            <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Modern legal consultation platform with real-time features
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '⚖️',
                title: 'Verified Expert Advocates',
                description: 'Connect with certified legal professionals specializing in criminal law, family court, corporate law, and more.'
              },
              {
                icon: '💬',
                title: 'Real-time Chat & Calls',
                description: 'Get immediate legal advice through secure WebSocket-powered real-time chat or voice calls. Pay per minute with transparent pricing.'
              },
              {
                icon: '🪙',
                title: 'Credit-Based Pricing System',
                description: 'Prepaid credit system with secure payment gateway. Get free trial credits and enjoy discount coupons.'
              },
              {
                icon: '⭐',
                title: 'Rating & Reviews',
                description: 'Choose advocates based on real client reviews and ratings. Make informed decisions.'
              },
              {
                icon: '🔒',
                title: 'Secure & Private',
                description: 'End-to-end encrypted consultations. Your legal matters remain confidential and secure.'
              },
              {
                icon: '📱',
                title: 'Multi-Device Support',
                description: 'Access legal help from anywhere - desktop, tablet, or mobile. Seamless experience across devices.'
              }
            ].map((feature, index) => (
              <div key={index} className={`p-6 rounded-xl transition-all hover:transform hover:scale-105 ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:shadow-lg'} shadow-md`}>
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
}

const About = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
      <section id="about" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/7841482/pexels-photo-7841482.jpeg" 
                alt="Legal Consultation" 
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">
                Revolutionizing <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Legal Access</span>
              </h2>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                BookMyVakeel bridges the gap between clients seeking legal help and qualified advocates ready to assist. Our platform ensures you get expert legal advice when you need it most.
              </p>
              <div className="space-y-4">
                {[
                  'Real-time chat with instant advocate connection',
                  'Transparent pricing with secure payment gateway',
                  'Secure communication and data protection',
                  'Quality assurance through ratings and reviews'
                ].map((point, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
}

const Contact = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
      <section id="contact" className={`py-20 ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">
            Ready to Get <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Legal Help</span>?
          </h2>
          <p className={`text-xl mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Join thousands of satisfied clients who found the legal expertise they needed.
          </p>
          <button
            onClick={handleAuthRedirect}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Consultation Today
          </button>
        </div>
      </section>
    );
}

const Footer = () => {
    const { darkMode, toggleDarkMode } = useTheme();

    return (
        <footer className={`py-12 border-t ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">BV</span>
                </div>
                <span className="text-xl font-bold">BookMyVakeel</span>
              </div>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Your trusted platform for instant legal consultation.
              </p>
              <div className='socials flex flex-row'>
                <a href="#" className='flex text-blue-500 hover:text-fuchsia-400 px-2'>Fb</a>
                <a href="#" className='flex text-blue-500 hover:text-fuchsia-400 px-2'>X</a>
                <a href="#" className='flex text-blue-500 hover:text-fuchsia-400 px-2'>Yt</a>
                <a href="#" className='flex text-blue-500 hover:text-fuchsia-400 px-2'>Ln</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Real-time Chat</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Legal Consultation</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Document Review</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Case Analysis</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Disclaimer</a></li>
              </ul>
            </div>
          </div>
          <div className={`mt-8 pt-8 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <p>&copy; {new Date().getFullYear()} BookMyVakeel. All rights reserved.</p>
          </div>
        </div>
      </footer>
    );
}

// Landing Page Component
const LandingPage = () => {
  const { darkMode, toggleDarkMode } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <Hero />

      {/* Features Section */}
      <Features />

      {/* About Section */}
      <About />

      {/* Contact Section */}
      <Contact />

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Profile page for handling auth callback
const ProfilePage = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError('No session ID found in URL');
          setLoading(false);
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Call backend to exchange session ID for user data
        const response = await axios.post(`${API}/auth/session`, {}, {
          headers: {
            'X-Session-ID': sessionId
          }
        });

        if (response.data.success) {
          await login(response.data.user, response.data.session_token);
          // Redirect based on user role
          window.location.href = '/app';
        } else {
          setError('Authentication failed');
        }
      } catch (error) {
        console.error('Auth error:', error);
        setError('Failed to authenticate user');
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [login]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Credit Purchase Modal
const CreditPurchaseModal = ({ isOpen, onClose, onPurchaseSuccess }) => {
  const { darkMode } = useTheme();
  const [packages, setPackages] = useState({});
  const [selectedPackage, setSelectedPackage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payments/packages`);
      setPackages(response.data.packages);
      setSelectedPackage(Object.keys(response.data.packages)[0] || '');
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-order`, {
        package_id: selectedPackage,
        origin_url: window.location.origin
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (response.data.success) {
        // For demo purposes, we'll simulate successful payment
        setTimeout(async () => {
          try {
            await axios.post(`${API}/payments/verify/${response.data.order_id}`, {}, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
              }
            });
            
            onPurchaseSuccess();
            onClose();
          } catch (error) {
            console.error('Payment verification failed:', error);
          }
        }, 2000);
        
        alert('Payment initiated! This is a demo - credits will be added shortly.');
      } else {
        alert('Failed to create payment order');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h3 className="text-2xl font-bold mb-6">Purchase Credits</h3>
        
        <div className="space-y-4 mb-6">
          {Object.entries(packages).map(([id, pkg]) => (
            <label key={id} className={`block p-4 border rounded-lg cursor-pointer ${
              selectedPackage === id 
                ? 'border-blue-600 bg-blue-50' + (darkMode ? ' dark:bg-blue-900' : '')
                : darkMode ? 'border-gray-600' : 'border-gray-300'
            }`}>
              <input
                type="radio"
                name="package"
                value={id}
                checked={selectedPackage === id}
                onChange={(e) => setSelectedPackage(e.target.value)}
                className="mr-3"
              />
              <span className="font-semibold">{pkg.name}</span>
              <div className="text-sm text-gray-600">
                ₹{pkg.amount} - {pkg.credits} credits
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={loading || !selectedPackage}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Purchase'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Real-time Chat Component
const ChatInterface = () => {
  const { consultationId } = useParams();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [consultation, setConsultation] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize socket connection
    const newSocket = io(BACKEND_URL, {
      auth: {
        token: localStorage.getItem('sessionToken')
      }
    });

    newSocket.on('connect', () => {
      setConnected(true);
      // Join consultation room
      newSocket.emit('join_consultation', { consultation_id: consultationId });
    });

    newSocket.on('disconnect', () => {
      setConnected(false);
    });

    newSocket.on('new_message', (messageData) => {
      setMessages(prev => [...prev, messageData]);
    });

    setSocket(newSocket);

    // Load existing messages
    loadMessages();

    return () => newSocket.close();
  }, [consultationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/${consultationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !socket || !connected) return;

    socket.emit('send_message', {
      consultation_id: consultationId,
      message: newMessage.trim()
    });

    setNewMessage('');
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Legal Consultation Chat</h2>
            <div className="flex items-center space-x-2 text-sm">
              <span className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
              message.sender_id === user?.id
                ? 'bg-blue-600 text-white'
                : darkMode 
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-900 shadow'
            }`}>
              <div className="text-sm font-medium mb-1">
                {message.sender_name} ({message.sender_role})
              </div>
              <div>{message.message}</div>
              <div className={`text-xs mt-1 ${
                message.sender_id === user?.id 
                  ? 'text-blue-200'
                  : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t p-4`}>
        <form onSubmit={sendMessage} className="flex space-x-4">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300'
            }`}
            disabled={!connected}
          />
          <button
            type="submit"
            disabled={!connected || !newMessage.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

// Main App Dashboard
const AppDashboard = () => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [advocates, setAdvocates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [showCreditModal, setShowCreditModal] = useState(false);

  useEffect(() => {
    if (user?.role === 'client') {
      fetchAdvocates();
      fetchCredits();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchAdvocates = async () => {
    try {
      const response = await axios.get(`${API}/advocates/available`);
      setAdvocates(response.data);
    } catch (error) {
      console.error('Failed to fetch advocates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits/balance`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const startConsultation = async (advocateId) => {
    try {
      const response = await axios.post(`${API}/consultations/start`, {
        advocate_id: advocateId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
      
      if (response.data.success) {
        // Redirect to chat interface
        window.location.href = `/chat/${response.data.consultation_id}`;
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Failed to start consultation:', error);
      alert('Failed to start consultation. Please try again.');
    }
  };

  const handlePurchaseSuccess = () => {
    fetchCredits();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Top Navigation */}
      <nav className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">BV</span>
              </div>
              <span className="text-xl font-bold">BookMyVakeel</span>
            </div>
            
            <div className="flex items-center space-x-4">
              {user?.role === 'client' && (
                <div className="flex items-center space-x-2">
                  <div className="credit-display">
                    💰 {credits.toFixed(0)} credits
                  </div>
                  <button
                    onClick={() => setShowCreditModal(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Buy Credits
                  </button>
                </div>
              )}
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Welcome, {user?.name}
              </span>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={logout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {user?.role === 'client' ? (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Available Advocates</h1>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Connect with verified legal experts for real-time consultation
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {advocates.map((advocate) => (
                <div key={advocate.id} className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex items-center mb-4">
                    <img 
                      src={advocate.profile_image || 'https://images.unsplash.com/photo-1662104935762-707db0439ecd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxsYXd5ZXJ8ZW58MHx8fGJsdWV8MTc1NDE5ODg3MHww&ixlib=rb-4.1.0&q=85'} 
                      alt={advocate.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="ml-4">
                      <h3 className="font-semibold text-lg">{advocate.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${advocate.is_online ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {advocate.is_online ? '🟢 Online' : '⚪ Offline'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <strong>Experience:</strong> {advocate.years_experience} years
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <strong>Specialization:</strong> {advocate.specialization}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <strong>Rate:</strong> ₹{advocate.rate_per_minute}/min
                    </p>
                    <div className="flex items-center">
                      <span className="text-yellow-400">
                        {'★'.repeat(Math.floor(advocate.rating))}
                        {'☆'.repeat(5 - Math.floor(advocate.rating))}
                      </span>
                      <span className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        ({advocate.total_reviews} reviews)
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => startConsultation(advocate.id)}
                    disabled={!advocate.is_online}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      advocate.is_online 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {advocate.is_online ? '💬 Start Chat' : 'Unavailable'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : user?.role === 'advocate' ? (
          <div className="text-center py-20">
            <h1 className="text-3xl font-bold mb-4">Advocate Dashboard</h1>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Welcome to your advocate dashboard. Real-time consultation management coming soon!
            </p>
          </div>
        ) : (
          <div className="text-center py-20">
            <h1 className="text-3xl font-bold mb-4">Admin Dashboard</h1>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Admin panel for managing the platform coming soon!
            </p>
          </div>
        )}
      </div>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal 
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Main App Component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route 
                path="/app" 
                element={
                  <ProtectedRoute>
                    <AppDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/chat/:consultationId" 
                element={
                  <ProtectedRoute>
                    <ChatInterface />
                  </ProtectedRoute>
                } 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;